import type { MenuItemBody } from '@/shared/api/generated/model';

/*
 * Правки дерева пунктов меню — чистые функции: экран отправляет на сервер
 * дерево целиком, а здесь только перестановки. Путь к пункту — индексы по
 * уровням: [1, 0] — первый вложенный во второй.
 */

export type Path = number[];

export const MAX_DEPTH = 3;

function childrenOf(item: MenuItemBody): MenuItemBody[] {
  return item.children ?? [];
}

/** Список, в котором лежит пункт, — копией вместе с родителями. */
function edit(
  items: MenuItemBody[],
  parent: Path,
  change: (list: MenuItemBody[]) => MenuItemBody[],
): MenuItemBody[] {
  if (parent.length === 0) {
    return change([...items]);
  }

  const [head, ...rest] = parent;

  return items.map((item, index) =>
    index === head ? { ...item, children: edit(childrenOf(item), rest, change) } : item,
  );
}

export function itemAt(items: MenuItemBody[], path: Path): MenuItemBody | undefined {
  let list = items;
  let found: MenuItemBody | undefined;

  for (const index of path) {
    found = list[index];

    if (found === undefined) {
      return undefined;
    }

    list = childrenOf(found);
  }

  return found;
}

export function update(items: MenuItemBody[], path: Path, next: MenuItemBody): MenuItemBody[] {
  const index = path.at(-1) ?? 0;

  return edit(items, path.slice(0, -1), (list) =>
    list.map((item, i) => (i === index ? next : item)),
  );
}

export function remove(items: MenuItemBody[], path: Path): MenuItemBody[] {
  const index = path.at(-1) ?? 0;

  return edit(items, path.slice(0, -1), (list) => list.filter((_, i) => i !== index));
}

export function append(items: MenuItemBody[], parent: Path, item: MenuItemBody): MenuItemBody[] {
  return edit(items, parent, (list) => [...list, item]);
}

/** Выше или ниже на своём уровне. */
export function move(items: MenuItemBody[], path: Path, by: -1 | 1): MenuItemBody[] {
  const index = path.at(-1) ?? 0;

  return edit(items, path.slice(0, -1), (list) => {
    const target = index + by;

    if (target < 0 || target >= list.length) {
      return list;
    }

    const [item] = list.splice(index, 1);

    if (item !== undefined) {
      list.splice(target, 0, item);
    }

    return list;
  });
}

function depthOf(item: MenuItemBody): number {
  return 1 + Math.max(0, ...childrenOf(item).map(depthOf));
}

/** Вложить в предыдущий пункт — последним его вложенным. */
export function indent(items: MenuItemBody[], path: Path): MenuItemBody[] {
  const index = path.at(-1) ?? 0;
  const item = itemAt(items, path);

  if (index === 0 || item === undefined || path.length + depthOf(item) > MAX_DEPTH) {
    return items;
  }

  const parent = path.slice(0, -1);
  const without = remove(items, path);

  return append(without, [...parent, index - 1], item);
}

/** Вынуть на уровень выше — сразу после бывшего родителя. */
export function outdent(items: MenuItemBody[], path: Path): MenuItemBody[] {
  if (path.length < 2) {
    return items;
  }

  const item = itemAt(items, path);

  if (item === undefined) {
    return items;
  }

  const parentPath = path.slice(0, -1);
  const parentIndex = parentPath.at(-1) ?? 0;

  return edit(remove(items, path), parentPath.slice(0, -1), (list) => {
    list.splice(parentIndex + 1, 0, item);

    return list;
  });
}

export function canIndent(items: MenuItemBody[], path: Path): boolean {
  const item = itemAt(items, path);

  return (path.at(-1) ?? 0) > 0 && item !== undefined && path.length + depthOf(item) <= MAX_DEPTH;
}
